import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatINR, formatDate } from "../lib/api";
import { ArrowLeft, Building2, User, Truck, Users, FileWarning } from "lucide-react";

const TYPE_META = {
  customer: { label: "Customer / Party", icon: Building2 },
  company: { label: "Company", icon: Building2 },
  department: { label: "Department", icon: Users },
  contact: { label: "Person / Contact", icon: User },
  driver: { label: "Driver", icon: Truck },
};

function Stat({ label, value, testId, accent }) {
  return (
    <div className={`border p-4 ${accent ? "bg-[#FBEAE7] border-[#E8B4AC]" : "bg-white border-[#E5E3DB]"}`}>
      <div className="text-[11px] uppercase tracking-wide text-[#68665E]">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${accent ? "text-[#8A2526]" : "text-[#1A1A18]"}`} data-testid={testId}>{value}</div>
    </div>
  );
}

function BillBadge({ status }) {
  if (status === "paid") return <span className="badge-success">Paid</span>;
  if (status === "overdue") return <span className="badge-error">Overdue</span>;
  if (status === "pending" || status === "submitted") return <span className="badge-pending">Bill Pending</span>;
  if (status === "not_generated") return <span className="inline-block px-2 py-0.5 text-[11px] font-semibold bg-[#FBEAE7] text-[#8A2526] border border-[#E8B4AC]">Bill Not Generated</span>;
  if (status === "cancelled") return <span className="badge-error">Cancelled</span>;
  return <span className="text-[#A09E96] text-xs">In transit</span>;
}

export default function EntityWork() {
  const { etype, key } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = etype === "customer"
      ? { type: "customer", name: decodeURIComponent(key) }
      : { type: etype, id: key, name: decodeURIComponent(key) };
    api.get("/entity-work", { params }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [etype, key]);

  if (!data) return <div className="text-sm text-[#68665E]">Loading…</div>;
  if (data.error) return <div className="text-sm text-[#8A2526]">Could not load this record.</div>;

  const s = data.summary;
  const meta = data.entity?.meta || {};
  const TM = TYPE_META[etype] || TYPE_META.customer;
  const Icon = TM.icon;
  const notGen = (data.lrs || []).filter((l) => l.bill_status === "not_generated");

  return (
    <div data-testid="entity-work-page">
      <button className="btn-ghost text-sm mb-4" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-center gap-3 mb-6">
        <Icon className="w-8 h-8 text-[#C84B31]" />
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">{TM.label}</div>
          <h1 className="font-headings text-3xl md:text-4xl font-bold text-[#1A1A18]" data-testid="entity-work-name">{data.entity?.name || "—"}</h1>
          <div className="text-sm text-[#68665E] mt-1">
            {[meta.phone, meta.email, meta.designation, meta.company_name, meta.department_name].filter(Boolean).join("  ·  ")}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
        <Stat label="LRs" value={s.lr_count} testId="ew-lr-count" />
        <Stat label="Delivered" value={s.delivered_count} testId="ew-delivered" />
        <Stat label="Total Freight" value={formatINR(s.total_freight)} testId="ew-freight" />
        <Stat label="Invoiced" value={formatINR(s.total_invoiced)} testId="ew-invoiced" />
        <Stat label="Paid" value={formatINR(s.paid)} testId="ew-paid" />
        <Stat label="Outstanding" value={formatINR(s.outstanding)} testId="ew-outstanding" />
        <Stat label="Bill Not Generated" value={s.not_generated_count} testId="ew-not-generated" accent />
      </div>

      {notGen.length > 0 && (
        <div className="mb-8 border border-[#E8B4AC] bg-[#FBEAE7]/40">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E8B4AC]">
            <FileWarning className="w-4 h-4 text-[#8A2526]" />
            <h2 className="font-headings text-base font-bold text-[#8A2526]">Delivered — Bill Not Generated ({notGen.length})</h2>
            <span className="text-sm text-[#8A2526] ml-auto font-medium">{formatINR(s.not_generated_freight)} un-billed</span>
          </div>
          <table className="w-full">
            <thead><tr><th className="table-th">LR #</th><th className="table-th">Date</th><th className="table-th">Route</th><th className="table-th">Freight</th><th className="table-th text-right">Action</th></tr></thead>
            <tbody>
              {notGen.map((lr) => (
                <tr key={lr.id} className="table-row" data-testid={`ew-notgen-${lr.id}`}>
                  <td className="table-td"><button className="text-[#C84B31] hover:underline font-mono text-xs" onClick={() => navigate(`/lr/${lr.id}`)}>{lr.lr_number}</button></td>
                  <td className="table-td">{formatDate(lr.lr_date)}</td>
                  <td className="table-td">{lr.from_location} -&gt; {lr.to_location}</td>
                  <td className="table-td font-medium">{formatINR(lr.freight_amount)}</td>
                  <td className="table-td text-right"><button className="btn-ghost text-xs text-[#C84B31]" onClick={() => navigate("/invoices")} data-testid={`ew-makebill-${lr.id}`}>Create Bill</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="font-headings text-lg font-bold mb-3">Lorry Receipts</h2>
      <div className="table-wrap mb-8">
        <table className="w-full">
          <thead><tr><th className="table-th">LR #</th><th className="table-th">Date</th><th className="table-th">Route</th><th className="table-th">Status</th><th className="table-th">Freight</th><th className="table-th">Bill</th></tr></thead>
          <tbody>
            {data.lrs.length === 0 ? <tr><td className="table-td text-center text-[#68665E]" colSpan={6}>No LRs.</td></tr> : data.lrs.map((lr) => (
              <tr key={lr.id} className="table-row" data-testid={`ew-lr-${lr.id}`}>
                <td className="table-td"><button className="text-[#C84B31] hover:underline font-mono text-xs" onClick={() => navigate(`/lr/${lr.id}`)}>{lr.lr_number}</button></td>
                <td className="table-td">{formatDate(lr.lr_date)}</td>
                <td className="table-td">{lr.from_location} -&gt; {lr.to_location}</td>
                <td className="table-td"><span className={lr.status === "delivered" ? "badge-success" : lr.status === "cancelled" ? "badge-error" : "badge-pending"}>{String(lr.status).replace("_", " ")}</span></td>
                <td className="table-td font-medium">{formatINR(lr.freight_amount)}</td>
                <td className="table-td"><BillBadge status={lr.bill_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-headings text-lg font-bold mb-3">Invoices</h2>
      <div className="table-wrap">
        <table className="w-full">
          <thead><tr><th className="table-th">Invoice #</th><th className="table-th">Date</th><th className="table-th">Total</th><th className="table-th">Status</th></tr></thead>
          <tbody>
            {data.invoices.length === 0 ? <tr><td className="table-td text-center text-[#68665E]" colSpan={4}>No invoices.</td></tr> : data.invoices.map((i) => (
              <tr key={i.id} className="table-row" data-testid={`ew-inv-${i.id}`}>
                <td className="table-td font-mono text-xs"><button className="hover:underline" onClick={() => navigate(`/invoices?status=${i.status}`)}>{i.invoice_number}</button></td>
                <td className="table-td">{formatDate(i.invoice_date)}</td>
                <td className="table-td font-medium">{formatINR(i.total)}</td>
                <td className="table-td"><span className={i.status === "paid" ? "badge-success" : i.status === "overdue" ? "badge-error" : "badge-pending"}>{i.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
